import React from 'react';

import goesJersey from '../assets/jerseys/Goes.png';
import MalvinJersey from '../assets/jerseys/Malvin.png';
import BiguaJersey from '../assets/jerseys/Bigua.png';
import cordonJersey from '../assets/jerseys/Cordon.png';
import aguadaJersey from '../assets/jerseys/Aguada.png';
import hebraicaJersey from '../assets/jerseys/Hebriaca.png';
import emptyJersey from '../assets/jerseys/Pisa SuperDt (19).png';
import defensorJersey from '../assets/jerseys/Defensor.png';
import penarolJersey from '../assets/jerseys/Penarol.png';
import unionJersey from '../assets/jerseys/Union.png';
import welcomeJersey from '../assets/jerseys/Welcome.png';
import nacionalJersey from '../assets/jerseys/Nacional.png';
import urundayJersey from '../assets/jerseys/Urunday.png';
import trouvilleJersey from '../assets/jerseys/Pisa SuperDt (28).png';

export const getJerseyImage = (teamName: string): string => {
  switch (teamName) {
    case 'Penarol':           return penarolJersey;
    case 'Nacional':          return nacionalJersey;
    case 'Defensor':          return defensorJersey;
    case 'Bigua':             return BiguaJersey;
    case 'Malvin':            return MalvinJersey;
    case 'Aguada':            return aguadaJersey;
    case 'Remeros Mercedes':
    case 'Goes':              return goesJersey;
    case 'Cordon':            return cordonJersey;
    case 'Hebraica y Macabi': return hebraicaJersey;
    case 'Welcome':           return welcomeJersey;
    case 'Urunday':
    case 'Urupan':            return urundayJersey;
    case 'Trouville':         return trouvilleJersey;
    case 'Union Atletica':    return unionJersey;
    default:                  return emptyJersey;
  }
};

// Foto del jugador centrada encima de la camiseta
const PlayerAvatar: React.FC<{
  sofascoreId: number;
  alt: string;
  isInactive?: boolean;
  circleSize: number;
}> = ({ sofascoreId, alt, isInactive, circleSize }) => {
  const [photoFailed, setPhotoFailed] = React.useState(false);
  if (photoFailed) return null;

  const photoSize = Math.round(circleSize * 0.72);
  const offset = Math.round((circleSize - photoSize) / 2);

  return (
    <div
      style={{
        position: 'absolute',
        top: offset, left: offset,
        width: photoSize, height: photoSize,
        borderRadius: '50%',
        overflow: 'hidden',
        border: '1.5px solid rgba(255,255,255,0.35)',
        background: '#0a1628',
      }}
    >
      <img
        src={`https://api.sofascore.app/api/v1/player/${sofascoreId}/image`}
        alt={alt}
        style={{
          width: '100%', height: '100%',
          objectFit: 'cover', objectPosition: 'top',
          filter: isInactive ? 'grayscale(1) opacity(0.5)' : undefined,
        }}
        onError={() => setPhotoFailed(true)}
      />
    </div>
  );
};

const shortenName = (name: string) => {
  const parts = name.trim().split(' ');
  if (parts.length <= 1) return name.toUpperCase();
  // Apellido primero, truncado
  const last = parts[parts.length - 1];
  return last.length > 7 ? last.substring(0, 7).toUpperCase() + '…' : last.toUpperCase();
};

interface JerseySlotProps {
  player?: {
    id: string | number;
    name: string;
    team: string;
    price: number;
    positionType?: string;
    isActive?: boolean;
    sofascoreId?: number | null;
  } | null;
  onClick?: () => void;
  isStarter?: boolean;
  isCaptain?: boolean;
  canChangeCaptain?: boolean;
  onSetCaptain?: () => void;
  slotPositionType?: string;
  displayPoints?: number;
  size?: 'sm' | 'bench' | 'md';
}

const JerseySlot: React.FC<JerseySlotProps> = ({
  player,
  onClick,
  isStarter = false,
  isCaptain = false,
  canChangeCaptain = false,
  onSetCaptain,
  slotPositionType,
  displayPoints,
  size = 'md',
}) => {
  const isInactive = player?.isActive === false;

  // Border color: captain=gold, inactive=red, starter=blue, bench=blue dimmer
  const borderColor = isCaptain
    ? '#FFB800'
    : isInactive
    ? '#ef4444'
    : isStarter
    ? '#0057B8'
    : '#1e3a5f';

  const cardW = size === 'sm' ? 68 : size === 'bench' ? 72 : 80;
  const circleSize = size === 'sm' ? 44 : size === 'bench' ? 48 : 52;

  const posLabel = slotPositionType === 'Interior' ? 'INT' : slotPositionType === 'Perimetral' ? 'PER' : '?';

  return (
    <div
      onClick={onClick}
      className="flex flex-col items-center cursor-pointer select-none"
      style={{ width: cardW }}
    >
      <div
        className="rounded-xl overflow-hidden flex flex-col items-center pb-1.5 relative transition-transform duration-150 active:scale-95"
        style={{
          width: cardW,
          background: 'rgba(13, 27, 46, 0.92)',
          border: `2px solid ${borderColor}`,
        }}
      >
        {/* Captain star */}
        {isCaptain && (
          <div className="absolute top-1 right-1 z-10 text-lub-accent text-[10px]">★</div>
        )}

        {/* Can set captain button */}
        {canChangeCaptain && !isCaptain && player && (
          <button
            onClick={e => { e.stopPropagation(); onSetCaptain?.(); }}
            className="absolute top-1 right-1 z-10 w-4 h-4 rounded-full bg-lub-blue text-white text-[8px] font-bold flex items-center justify-center"
          >C</button>
        )}

        {/* Jersey / avatar circle */}
        <div
          className="mt-2 rounded-full overflow-hidden flex-shrink-0"
          style={{
            width: circleSize,
            height: circleSize,
            border: `2px solid ${borderColor}`,
            background: '#0a1628',
            position: 'relative',
          }}
        >
          {player ? (
            <>
              {/* Camiseta siempre de fondo */}
              <img
                src={getJerseyImage(player.team)}
                alt=""
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  objectFit: 'contain',
                  filter: isInactive ? 'grayscale(1) opacity(0.5)' : undefined,
                }}
              />
              {/* Foto del jugador encima, si tiene sofascoreId */}
              {player.sofascoreId && (
                <PlayerAvatar
                  sofascoreId={player.sofascoreId}
                  alt={player.name}
                  isInactive={isInactive}
                  circleSize={circleSize}
                />
              )}
            </>
          ) : (
            <span
              style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              className="text-xs font-bold text-white/30"
            >{posLabel}</span>
          )}
        </div>

        {/* Name */}
        <p
          className="mt-1.5 px-1 text-center font-bold leading-none"
          style={{
            fontSize: size === 'md' ? 10 : 9,
            color: isInactive ? '#f87171' : 'white',
            maxWidth: cardW - 4,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
        >
          {player ? shortenName(player.name) : '+'}
        </p>

        {/* Points / price */}
        <p
          className="font-semibold leading-none mt-0.5"
          style={{
            fontSize: size === 'md' ? 10 : 9,
            color: displayPoints !== undefined && displayPoints > 0
              ? '#4ade80'
              : 'rgba(255,255,255,0.45)',
          }}
        >
          {player
            ? displayPoints !== undefined
              ? `${displayPoints} PTS`
              : `$${player.price}M`
            : ''}
        </p>
      </div>
    </div>
  );
};

export default JerseySlot;
